import {
  Availabilities,
  DayAvailability,
  PartnerInfo,
  PartnerServiceInfo,
  ServiceInfo,
  BookAppointmentResponse,
  PatientAppointmentsResponse,
  PatientAppointmentItem,
  SlotStatusHistory,
  StatusUpdatedByRole,
  SlotStatusType,
} from '../models'
import {AssertTypeFn} from './general'
import {
  assertArray,
  assertNumber,
  assertString,
  assertStringOrNull,
  assertEnumOrNull,
  assertEnum,
  assertObjectOrUndefined,
} from '../utils'
import {questionnaireNormaliser} from './questionnaires'

export const partnerInfoNormaliser: AssertTypeFn<PartnerInfo> = (data) => ({
  name: assertString(data.name),
  logoUrl: assertStringOrNull(data.logoUrl),
  address: assertStringOrNull(data.address),
  phone: assertStringOrNull(data.phone),
  website: assertStringOrNull(data.website),
  email: assertStringOrNull(data.email),
  termsAndConditionsUrl: assertStringOrNull(data.termsAndConditionsUrl),
  privacyPolicyUrl: assertStringOrNull(data.privacyPolicyUrl),
  timeZoneId: assertStringOrNull(data.timeZoneId) ?? 'UTC',
})

export const partnerServiceInfoNormaliser: AssertTypeFn<PartnerServiceInfo> = (
  data,
) => ({
  name: assertString(data.name),
  internalCode: assertStringOrNull(data.internalCode),
  location: assertStringOrNull(data.location),
})

export const serviceInfoNormaliser: AssertTypeFn<ServiceInfo> = (data) => ({
  partner: partnerInfoNormaliser(data.partner),
  partnerService: partnerServiceInfoNormaliser(data.partnerService),
  preQuestionnaire: assertObjectOrUndefined(
    data.preQuestionnaire,
    questionnaireNormaliser,
  ),
})

export const dayAvailabilityNormaliser: AssertTypeFn<DayAvailability> = (
  data,
) => ({
  date: assertString(data.date),
  day: assertString(data.day),
  slots: assertArray(data.slots, assertString),
})

export const availabilitiesNormaliser: AssertTypeFn<Availabilities> = (
  data,
) => ({
  month: assertNumber(data.month),
  yearMonth: assertString(data.yearMonth),
  days: assertArray(data.days, (d) => dayAvailabilityNormaliser(d)),
})

export const bookAppointmentResponseNormaliser: AssertTypeFn<
  BookAppointmentResponse
> = (data) => ({
  date: assertString(data.date),
  time: assertString(data.time),
  serviceName: assertString(data.serviceName),
  partnerServiceSlotId: assertString(data.partnerServiceSlotId),
  patientId: assertString(data.patientId),
  patientEmail: assertString(data.patientEmail),
  patientFirstName: assertStringOrNull(data.patientFirstName),
  patientSecondName: assertStringOrNull(data.patientSecondName),
  patientPhone: assertStringOrNull(data.patientPhone),
})

export const slotStatusHistoryNormaliser: AssertTypeFn<SlotStatusHistory> = (
  data,
) => ({
  at: assertString(data.at),
  status: assertEnum<SlotStatusType>(data.status, SlotStatusType),
  comment: assertStringOrNull(data.comment),
  by: assertEnumOrNull(data.by, StatusUpdatedByRole),
})

export const patientAppointmentItemNormaliser: AssertTypeFn<
  PatientAppointmentItem
> = (data) => ({
  date: assertString(data.date),
  time: assertString(data.time),
  slotId: assertString(data.slotId),
  partnerServiceId: assertString(data.partnerServiceId),
  status: assertEnum<SlotStatusType>(data.status, SlotStatusType),
  serviceName: assertStringOrNull(data.serviceName),
  history: assertArray(data.history, slotStatusHistoryNormaliser),
})

export const patientAppointmentsResponseNormaliser: AssertTypeFn<
  PatientAppointmentsResponse
> = (data) => ({
  items: assertArray(data.items, patientAppointmentItemNormaliser),
  partner: partnerInfoNormaliser(data.partner),
})
